import type {
  Heading,
  Image,
  InlineCode,
  Link,
  Paragraph,
  PhrasingContent,
  Root,
  RootContent,
  Table,
  TableCell,
  TableRow,
  Text,
} from "mdast";
import remarkGfm from "remark-gfm";
import remarkParse from "remark-parse";
import { unified } from "unified";
import {
  ImageContent,
  MarkdownBlockKind,
  NormalizedInlineSegment,
  NormalizedMarkdownBlock,
  NormalizedTableBlock,
  NormalizedTableCell,
  TableObject,
} from "./markdownDiffTypes";

const markdownParser = unified().use(remarkParse).use(remarkGfm);

export const normalizeHeaderCell = (headerCell: string): string =>
  headerCell
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const createHeaderSignature = (headerCells: string[]): string =>
  headerCells.map(normalizeHeaderCell).join("|");

export const parseMarkdownBlocks = (
  markdown: string
): NormalizedMarkdownBlock[] => {
  const tree = markdownParser.parse(markdown) as Root;

  const blocks = tree.children
    .map((node) => createBlockFromNode(node, markdown))
    .filter((block): block is NormalizedMarkdownBlock => block !== null);

  if (blocks.length === 0 && markdown.trim() !== "") {
    return [createTextBlock("paragraph", markdown.trim())];
  }

  return blocks;
};

export const extractMarkdownContent = (
  markdown: string
): {
  markdownWithoutContent: string;
  tables: Array<TableObject>;
  images: Array<ImageContent>;
} => {
  const blocks = parseMarkdownBlocks(markdown);

  return {
    markdownWithoutContent: markdown,
    tables: blocks
      .filter((block) => block.kind === "table" && block.table)
      .map((block, index) => normalizedTableToTableObject(block.table!, index)),
    images: blocks
      .filter((block) => block.kind === "image" || block.kind === "link")
      .map((block, index) => ({
        image: block.rawMarkdown,
        placeholder: block.imageUrl || `MEDIA_${index}`,
        imageUrl: block.imageUrl || null,
      })),
  };
};

export const parseMarkdownTable = (
  tableMarkdown: string
): NormalizedTableBlock => {
  const blocks = parseMarkdownBlocks(tableMarkdown);
  const tableBlock = blocks.find(
    (block) => block.kind === "table" && block.table
  );

  if (tableBlock?.table) {
    return tableBlock.table;
  }

  return {
    headerCells: [],
    headerTexts: [],
    normalizedHeaders: [],
    rows: [],
    rowTexts: [],
    rawMarkdown: tableMarkdown.trim(),
    headerSignature: "",
    columnCount: 0,
  };
};

export const hasSemanticInlineSegments = (
  inlineSegments?: NormalizedInlineSegment[]
): boolean =>
  Boolean(
    inlineSegments?.some(
      (segment) =>
        segment.kind === "link" ||
        segment.kind === "image" ||
        segment.kind === "inlineCode"
    )
  );

const createBlockFromNode = (
  node: RootContent,
  markdown: string
): NormalizedMarkdownBlock | null => {
  const rawMarkdown = getNodeMarkdown(markdown, node).trim();
  if (!rawMarkdown) {
    return null;
  }

  switch (node.type) {
    case "table":
      return createTableBlock(node, rawMarkdown, markdown);
    case "heading":
      return createPhrasingBlock("heading", node, rawMarkdown, markdown);
    case "paragraph":
      return createParagraphBlock(node, rawMarkdown, markdown);
    case "list":
      return createTextBlock("list", rawMarkdown);
    case "code":
      return createTextBlock("code", rawMarkdown);
    case "blockquote":
      return createTextBlock("quote", rawMarkdown);
    case "html":
      return createTextBlock("html", rawMarkdown);
    case "thematicBreak":
      return createTextBlock("thematic-break", rawMarkdown);
    default:
      return createTextBlock("paragraph", rawMarkdown);
  }
};

const createTableBlock = (
  node: Table,
  rawMarkdown: string,
  markdown: string
): NormalizedMarkdownBlock => {
  const rows = node.children as TableRow[];
  const headerCells = extractTableRowCells(rows[0], markdown);
  const dataRows = rows
    .slice(1)
    .map((row) => extractTableRowCells(row, markdown));
  const headerTexts = headerCells.map((cell) => cell.plainText);
  const rowTexts = dataRows.map((row) => row.map((cell) => cell.plainText));
  const table = {
    headerCells,
    headerTexts,
    normalizedHeaders: headerTexts.map(normalizeHeaderCell),
    rows: dataRows,
    rowTexts,
    rawMarkdown,
    headerSignature: createHeaderSignature(headerTexts),
    columnCount: headerCells.length,
  };

  return {
    kind: "table",
    rawMarkdown,
    normalizedContent: normalizeWhitespace(rawMarkdown),
    exactSignature: `table:${normalizeWhitespace(rawMarkdown)}`,
    table,
  };
};

const createParagraphBlock = (
  node: Paragraph,
  rawMarkdown: string,
  markdown: string
): NormalizedMarkdownBlock => {
  const delimiterlessTable = createDelimiterlessTableBlock(rawMarkdown);
  if (delimiterlessTable) {
    return delimiterlessTable;
  }

  const standaloneMedia = getStandaloneMedia(node);
  if (standaloneMedia) {
    return {
      kind: standaloneMedia.kind,
      rawMarkdown,
      normalizedContent:
        standaloneMedia.url || normalizeWhitespace(rawMarkdown),
      exactSignature: `${standaloneMedia.kind}:${
        standaloneMedia.url || normalizeWhitespace(rawMarkdown)
      }`,
      imageUrl: standaloneMedia.url,
      mediaLabel: standaloneMedia.label,
      mediaTitle: standaloneMedia.title,
    };
  }

  return createPhrasingBlock("paragraph", node, rawMarkdown, markdown);
};

const createPhrasingBlock = (
  kind: Exclude<MarkdownBlockKind, "table" | "image" | "link">,
  node: Paragraph | Heading,
  rawMarkdown: string,
  markdown: string
): NormalizedMarkdownBlock => {
  const inlineSegments = extractInlineSegmentsFromChildren(
    markdown,
    node.children
  );

  return createTextBlock(
    kind,
    rawMarkdown,
    hasSemanticInlineSegments(inlineSegments) ? inlineSegments : undefined
  );
};

const createTextBlock = (
  kind: Exclude<MarkdownBlockKind, "table" | "image" | "link">,
  rawMarkdown: string,
  inlineSegments?: NormalizedInlineSegment[]
): NormalizedMarkdownBlock => ({
  kind,
  rawMarkdown,
  normalizedContent: normalizeWhitespace(rawMarkdown),
  exactSignature: `${kind}:${normalizeWhitespace(rawMarkdown)}`,
  inlineSegments,
});

const createDelimiterlessTableBlock = (
  rawMarkdown: string
): NormalizedMarkdownBlock | null => {
  const lines = rawMarkdown
    .trim()
    .split(/\r?\n/)
    .map((line) => line.trim());
  const rows = lines.map(parseDelimiterlessTableRow);

  if (
    lines.length === 0 ||
    rows.some((row) => row === null) ||
    new Set(rows.map((row) => row?.length)).size !== 1
  ) {
    return null;
  }

  const parsedRows = rows as string[][];
  const columnCount = parsedRows[0].length;
  if (columnCount < 2) {
    return null;
  }

  const rowTexts = parsedRows;
  const tableRows = rowTexts.map((row) =>
    row.map(createDelimiterlessTableCell)
  );
  const table = {
    headerCells: [],
    headerTexts: [],
    normalizedHeaders: [],
    rows: tableRows,
    rowTexts,
    rawMarkdown,
    headerSignature: "",
    columnCount,
    isHeaderless: true,
  };

  return {
    kind: "table",
    rawMarkdown,
    normalizedContent: normalizeWhitespace(rawMarkdown),
    exactSignature: `table:${normalizeWhitespace(rawMarkdown)}`,
    table,
  };
};

const parseDelimiterlessTableRow = (line: string): string[] | null => {
  if (!line.startsWith("|") || !line.endsWith("|")) {
    return null;
  }

  return splitUnescapedPipes(line.slice(1, -1)).map((cell) =>
    cell.trim().replace(/\\\|/g, "|")
  );
};

const splitUnescapedPipes = (value: string): string[] => {
  const cells: string[] = [];
  let currentCell = "";

  for (let index = 0; index < value.length; index += 1) {
    const character = value[index];
    if (character === "|" && value[index - 1] !== "\\") {
      cells.push(currentCell);
      currentCell = "";
      continue;
    }

    currentCell += character;
  }

  cells.push(currentCell);
  return cells;
};

const createDelimiterlessTableCell = (value: string): NormalizedTableCell => ({
  rawMarkdown: value,
  plainText: value,
  normalizedText: normalizeWhitespace(value),
});

const getStandaloneMedia = (
  node: Paragraph
): {
  kind: "image" | "link";
  url: string;
  label: string;
  title: string | null;
} | null => {
  const meaningfulChildren = node.children.filter(
    (child) => child.type !== "text" || child.value.trim() !== ""
  );

  if (meaningfulChildren.length !== 1) {
    return null;
  }

  const child = meaningfulChildren[0];
  if (child.type === "image") {
    const imageChild = child as Image;
    return {
      kind: "image",
      url: imageChild.url,
      label: imageChild.alt || imageChild.url,
      title: imageChild.title || null,
    };
  }

  if (child.type === "link") {
    const linkChild = child as Link;
    return {
      kind: "link",
      url: linkChild.url,
      label: extractNodeText(linkChild).trim() || linkChild.url,
      title: linkChild.title || null,
    };
  }

  return null;
};

const extractTableRowCells = (
  row: TableRow | undefined,
  markdown: string
): NormalizedTableCell[] => {
  if (!row) {
    return [];
  }

  return row.children.map((cell) => createTableCell(cell, markdown));
};

const createTableCell = (
  cell: TableCell,
  markdown: string
): NormalizedTableCell => {
  const rawMarkdown = cleanTableCellMarkdown(getNodeMarkdown(markdown, cell));
  const plainText = extractNodeText(cell).trim();
  const inlineSegments = extractInlineSegmentsFromChildren(
    markdown,
    cell.children as PhrasingContent[]
  );

  return {
    rawMarkdown: rawMarkdown || plainText,
    plainText,
    normalizedText: normalizeWhitespace(plainText),
    inlineSegments: inlineSegments.length > 0 ? inlineSegments : undefined,
  };
};

const extractInlineSegmentsFromChildren = (
  markdown: string,
  children: PhrasingContent[]
): NormalizedInlineSegment[] =>
  mergeAdjacentTextSegments(
    children
      .flatMap((child) => createInlineSegmentsFromNode(markdown, child))
      .filter(
        (segment) =>
          segment.rawMarkdown !== "" ||
          segment.plainText !== "" ||
          segment.kind === "break"
      )
  );

const createInlineSegmentsFromNode = (
  markdown: string,
  node: PhrasingContent
): NormalizedInlineSegment[] => {
  const rawMarkdown = getNodeMarkdown(markdown, node);

  switch (node.type) {
    case "text":
      return [
        createInlineSegment(
          "text",
          rawMarkdown || (node as Text).value,
          (node as Text).value
        ),
      ];
    case "break":
      return [createInlineSegment("break", rawMarkdown || "  \n", " ")];
    case "inlineCode":
      return [
        createInlineSegment(
          "inlineCode",
          rawMarkdown || `\`${(node as InlineCode).value}\``,
          (node as InlineCode).value
        ),
      ];
    case "image": {
      const imageNode = node as Image;
      return [
        createInlineSegment(
          "image",
          rawMarkdown || `![${imageNode.alt || ""}](${imageNode.url})`,
          imageNode.alt || imageNode.url,
          {
            url: imageNode.url,
            label: imageNode.alt || imageNode.url,
            title: imageNode.title || null,
          }
        ),
      ];
    }
    case "link": {
      const linkNode = node as Link;
      const label = extractNodeText(linkNode).trim() || linkNode.url;
      return [
        createInlineSegment("link", rawMarkdown || label, label, {
          url: linkNode.url,
          label,
          title: linkNode.title || null,
        }),
      ];
    }
    default: {
      const plainText = extractNodeText(node);
      return [
        createInlineSegment("fallback", rawMarkdown || plainText, plainText),
      ];
    }
  }
};

const createInlineSegment = (
  kind: NormalizedInlineSegment["kind"],
  rawMarkdown: string,
  plainText: string,
  media?: {
    url?: string | null;
    label?: string;
    title?: string | null;
  }
): NormalizedInlineSegment => ({
  kind,
  rawMarkdown,
  plainText,
  normalizedText: normalizeWhitespace(plainText),
  exactSignature: `${kind}:${normalizeWhitespace(rawMarkdown)}`,
  url: media?.url,
  label: media?.label,
  title: media?.title,
});

const mergeAdjacentTextSegments = (
  segments: NormalizedInlineSegment[]
): NormalizedInlineSegment[] => {
  const merged: NormalizedInlineSegment[] = [];

  segments.forEach((segment) => {
    const previousSegment = merged[merged.length - 1];
    if (
      previousSegment &&
      previousSegment.kind === "text" &&
      segment.kind === "text"
    ) {
      previousSegment.rawMarkdown += segment.rawMarkdown;
      previousSegment.plainText += segment.plainText;
      previousSegment.normalizedText = normalizeWhitespace(
        previousSegment.plainText
      );
      previousSegment.exactSignature = `text:${normalizeWhitespace(
        previousSegment.rawMarkdown
      )}`;
      return;
    }

    merged.push({ ...segment });
  });

  return merged;
};

const extractNodeText = (node: any): string => {
  switch (node.type) {
    case "text":
      return node.value;
    case "inlineCode":
      return node.value;
    case "break":
      return " ";
    case "image":
      return node.alt || node.url;
    default:
      if (Array.isArray(node.children)) {
        return node.children.map(extractNodeText).join("");
      }

      if (typeof node.value === "string") {
        return node.value;
      }

      return "";
  }
};

const getNodeMarkdown = (
  markdown: string,
  node: {
    position?: {
      start?: { offset?: number };
      end?: { offset?: number };
    };
  }
): string => {
  const startOffset = node.position?.start?.offset;
  const endOffset = node.position?.end?.offset;

  if (typeof startOffset === "number" && typeof endOffset === "number") {
    return markdown.slice(startOffset, endOffset);
  }

  return "";
};

const cleanTableCellMarkdown = (value: string): string =>
  value
    .trim()
    .replace(/^\|\s*/, "")
    .replace(/\s*\|$/, "")
    .trim();

const normalizedTableToTableObject = (
  table: NormalizedTableBlock,
  index: number
): TableObject => ({
  headerCells: table.headerTexts,
  normalizedHeaders: table.normalizedHeaders,
  rowCells: table.rowTexts,
  rows: table.rowTexts.map((row) => `| ${row.join(" | ")} |`),
  table: table.rawMarkdown,
  placeholder: table.isHeaderless
    ? `TABLE_ROWS_${index}`
    : table.headerTexts.length >= 2
      ? `TABLE_${table.headerTexts[0].replace(/\s/g, "_")}_${table.headerTexts[1].replace(/\s/g, "_")}`
      : `TABLE_GENERIC_${index}`,
  columnCount: table.columnCount,
  isHeaderless: table.isHeaderless,
});

const normalizeWhitespace = (value: string): string =>
  value.replace(/\s+/g, " ").trim();
